import { ColDef, RowClickedEvent, themeAlpine } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import sumBy from "lodash.sumby";
import { DateTime } from "luxon";
import React, { useMemo, useRef } from "react";
import { useImmer } from "use-immer";

import { IgTableWrapper } from "@intelligems/ig-theme";
import { OrganizationSummaryDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import {
  CurrencyFormat,
  getBillingCycleValue,
  getStatusFromAdminOrg,
} from "@src/features/admin/components/utils";
import { BillingCycleColumn } from "@src/features/admin/pages/overview/components/Tables/OrganizationOverviewTable/renderers/BillingCycleColumn";
import { BillingProviderColumn } from "@src/features/admin/pages/overview/components/Tables/OrganizationOverviewTable/renderers/BillingProviderColumn";
import { DetailsRenderer } from "@src/features/admin/pages/overview/components/Tables/OrganizationOverviewTable/renderers/DetailsRenderer";
import { IconColumn } from "@src/features/admin/pages/overview/components/Tables/OrganizationOverviewTable/renderers/IconColumn";
import { SearchBar } from "@src/features/admin/pages/overview/components/Tables/OrganizationOverviewTable/SearchBar";
import { OrganizationOverviewFilter } from "@src/features/admin/pages/overview/components/Tables/OrganizationOverviewTable/types";
import { theme } from "@src/theme";

interface OrganizationBillingTableProps {
  orgs: OrganizationSummaryDto[];
  hideSearch?: boolean;
  headerHeight?: number;
  minHeight?: string;
  headerColor?: string;
}
const filterKeys = ["billingProvider", "status", "interval"];

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
});

export const OrganizationOverviewTable = ({
  orgs,
  hideSearch,
  headerHeight,
  minHeight,
  headerColor,
}: OrganizationBillingTableProps) => {
  const gridRef = useRef<AgGridReact>(null);
  const [filters, setFilters] = useImmer<OrganizationOverviewFilter>({
    search: "",
    billingProvider: "all",
    status: "all",
    interval: "all",
  });

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
    };
  }, []);

  const columnDefs: ColDef<OrganizationSummaryDto>[] = useMemo(
    () => [
      {
        headerName: "",
        cellRenderer: BillingProviderColumn,
        maxWidth: 85,
      },

      {
        headerName: "Name",
        field: "name",
        sortable: true,
        filter: "agTextColumnFilter",
        cellStyle: { justifyContent: "start" },
      },
      {
        headerName: "Created On",
        sort: "desc",
        field: "createdAtTs",
        sortable: true,
        maxWidth: 150,
        valueFormatter: (params) => {
          return DateTime.fromMillis(params.value).toFormat("yyyy-MM-dd");
        },
      },
      {
        headerName: "Total",
        sortable: true,
        valueGetter: (params) => {
          if (params.data?.agencies?.length) {
            return "-";
          }
          const slTotal = sumBy(
            params.data?.stripeSubscriptionSummaries || [],
            (s) =>
              sumBy(
                s.stripeProductLinks || [],
                (p) => p.stripeProduct?.flatPrice || 0
              )
          );

          const osTotal = sumBy(
            params.data?.organizationSubscriptions || [],
            (s) => s.price || 0
          );

          const total = slTotal + osTotal;
          return total ? CurrencyFormat.format(total) : "-";
        },
      },
      {
        headerName: "Billing Interval",
        cellRenderer: BillingCycleColumn,
        filter: "agTextColumnFilter",
        sortable: true,
        filterValueGetter: (params) => {
          if (!params.data) {
            return null;
          }
          return getBillingCycleValue(
            params.data.stripeSubscriptionSummaries,
            params.data.organizationSubscriptions
          );
        },
      },
      {
        headerName: "",
        cellRenderer: IconColumn,
        maxWidth: 300,
        filter: "agTextColumnFilter",
        sortable: true,
        valueGetter: (data) => getStatusFromAdminOrg(data.data),
      },
      {
        headerName: "",
        showRowGroup: true,
        maxWidth: 100,
        cellStyle: { justifyContent: "end" },
        cellRenderer: "agGroupCellRenderer",
      },
    ],
    []
  );

  const onRowClick = (event: RowClickedEvent<OrganizationSummaryDto>) => {
    event.node.setExpanded(!event.node.expanded);
  };

  const data = useMemo(() => {
    let filterResp = orgs.filter((org) => {
      if (!filters.search) {
        return true;
      }
      return (
        org.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        org.id.toLowerCase().includes(filters.search.toLowerCase()) ||
        org.shopId.toLowerCase().includes(filters.search.toLowerCase())
      );
    });

    for (const key of filterKeys) {
      filterResp = filterResp.filter((org) => {
        if (filters[key] === "all") {
          return true;
        } else {
          return (
            org.stripeSubscriptionSummaries.some(
              (s) => s[key] === filters[key]
            ) ||
            org.organizationSubscriptions.some((s) => s[key] === filters[key])
          );
        }
      });
    }

    return filterResp;
  }, [filters, orgs]);

  return (
    <Grid
      container
      item
      direction={"column"}
      rowSpacing={2}
      justifyContent={"space-between"}
    >
      {hideSearch ? null : (
        <SearchBar filters={filters} setFilters={setFilters} orgs={orgs} />
      )}
      <Grid item id={"organization-overview-table-wrapper"}>
        <IgTableWrapper height={null} minHeight={minHeight || "80vh"}>
          <AgGridReact<OrganizationSummaryDto>
            theme={myTheme}
            rowData={data}
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            detailCellRenderer={DetailsRenderer}
            masterDetail={true}
            domLayout={"autoHeight"}
            detailRowAutoHeight={true}
            headerHeight={headerHeight || 40}
            suppressCellFocus={true}
            onRowClicked={onRowClick}
            pagination={true}
            paginationPageSize={20}
          />
        </IgTableWrapper>
      </Grid>
    </Grid>
  );
};
